"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useState } from "react";
import type { NewsletterModel } from "@/app/[domain]/(sites)/_actions/newsletter/newsletter.types";
import { RegularButton } from "@/components/Button/RegularButton/RegularButton.component";
import { HoneypotInput } from "@/components/HoneypotField/HoneypotField.component";
import { Email } from "@/components/Input/Email/Email.component";
import { Slider } from "@/components/Slider/Slider.component";
import { SliderItem } from "@/components/Slider/SliderItem.component";
import { NewsletterTermsCheckbox } from "@/components/Terms/NewsletterTermsCheckbox/NewsletterTermsCheckbox.component";
import { Translation } from "@/components/Translation/Translation.component";
import { useNewsletterForm } from "@/hooks/use-newsletter-form.hook";
import type { ArticleContentModel } from "@/services/api/content/models/article-content-model.types";
type NewsletterSignUpFormProps = {
  readonly model: NewsletterModel;
  readonly isPending: boolean;
  readonly onSubmit: (email: string, isTermsAccepted: boolean, honeypotValue: string | undefined) => void;
  readonly variant?: "accent" | "primary";
  readonly renderImage: () => ReactNode;
  readonly permissionText: ReadonlyArray<ArticleContentModel.BodyTextElement>;
};
export function NewsletterSignUpForm(t0) {
  const $ = _c(54);
  const {
    isPending,
    model: t1,
    onSubmit,
    renderImage,
    permissionText,
    variant: t2
  } = t0;
  const {
    description
  } = t1;
  const variant = t2 === undefined ? "primary" : t2;
  const [activeStep, setActiveStep] = useState(0);
  const {
    isFormValid,
    isEmailValid,
    honeypotRef,
    onEmailChange,
    onTermsChange,
    handleSubmit
  } = useNewsletterForm(onSubmit);
  let t3;
  if ($[0] !== honeypotRef) {
    t3 = <HoneypotInput ref={honeypotRef} />;
    $[0] = honeypotRef;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = activeStep !== 0;
  let t5;
  if ($[2] !== renderImage) {
    t5 = renderImage();
    $[2] = renderImage;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t5) {
    t6 = <div className="relative col-span-full h-20 sm:order-2 sm:col-span-5 sm:h-full">{t5}</div>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h1 className="mb-1 text-balance font-theme-bold text-2xl tracking-tight lg:text-3xl"><Translation model={{
        da: "Tilmeld dig vores nyhedsbrev",
        de: "Zum Newsletter anmelden",
        en: "Sign up for our newsletter",
        no: "Meld deg p\xE5 v\xE5rt nyhetsbrev",
        sv: "Prenumerera p\xE5 v\xE5rt nyhetsbrev"
      }} /></h1>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== description) {
    t8 = <div>{t7}<p className="mb-4 leading-snug">{description}</p></div>;
    $[7] = description;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== isEmailValid) {
    t9 = event => {
      if (event.key === "Enter" && isEmailValid) {
        setActiveStep(_temp);
      }
    };
    $[9] = isEmailValid;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isPending || $[12] !== onEmailChange || $[13] !== t9) {
    t10 = <div className="w-full"><Email disabled={isPending} name="email" onChange={onEmailChange} onKeyDown={t9} required={true} /></div>;
    $[11] = isPending;
    $[12] = onEmailChange;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = !isEmailValid;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setActiveStep(_temp2);
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Translation da={"Forts\xE6t"} de="Weiter" en="Continue" no="Fortsett" sv={"Forts\xE4tt"} />;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t11 || $[18] !== variant) {
    t14 = <RegularButton disabled={t11} onClick={t12} testId="continue-newsletter-button" variant={variant}>{t13}</RegularButton>;
    $[17] = t11;
    $[18] = variant;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t10 || $[21] !== t14) {
    t15 = <div className="flex flex-col gap-x-1 gap-y-2 xl:flex-row">{t10}{t14}</div>;
    $[20] = t10;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15 || $[24] !== t8) {
    t16 = <div className="col-span-full flex shrink flex-col justify-between p-2 sm:col-span-7 sm:basis-full sm:py-3">{t8}{t15}</div>;
    $[23] = t15;
    $[24] = t8;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t16 || $[27] !== t4 || $[28] !== t6) {
    t17 = <SliderItem className="grid grid-cols-12" isDisabled={t4}>{t6}{t16}</SliderItem>;
    $[26] = t16;
    $[27] = t4;
    $[28] = t6;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const t18 = activeStep !== 1;
  let t19;
  if ($[30] !== isPending || $[31] !== onTermsChange || $[32] !== permissionText || $[33] !== variant) {
    t19 = <NewsletterTermsCheckbox isDisabled={isPending} isRequired={true} onChange={onTermsChange} permissionText={permissionText} variant={variant} />;
    $[30] = isPending;
    $[31] = onTermsChange;
    $[32] = permissionText;
    $[33] = variant;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setActiveStep(_temp3);
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <RegularButton className="w-1/2 lg:w-fit" onClick={t20} testId="back-newsletter-button" variant="outline"><Translation da="Tilbage" de={"Zur\xFCck"} en="Back" no="Tilbake" sv="Tillbaka" /></RegularButton>;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const t22 = !isFormValid;
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Translation model={{
      da: "Tilmeld",
      de: "Anmelden",
      en: "Subscribe",
      no: "Registrer",
      sv: "Prenumerera"
    }} />;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== isPending || $[39] !== t22 || $[40] !== variant) {
    t24 = <div className="flex justify-end gap-1">{t21}<RegularButton className="w-1/2 lg:w-fit" disabled={t22} isPending={isPending} testId="submit-newsletter-button" type="submit" variant={variant}>{t23}</RegularButton></div>;
    $[38] = isPending;
    $[39] = t22;
    $[40] = variant;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t18 || $[43] !== t19 || $[44] !== t24) {
    t25 = <SliderItem className="flex flex-col justify-between gap-y-2 px-2 pt-4 pb-3" isDisabled={t18}>{t19}{t24}</SliderItem>;
    $[42] = t18;
    $[43] = t19;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== activeStep || $[47] !== t17 || $[48] !== t25) {
    t26 = <Slider activeStep={activeStep}>{t17}{t25}</Slider>;
    $[46] = activeStep;
    $[47] = t17;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== handleSubmit || $[51] !== t26 || $[52] !== t3) {
    t27 = <form data-testid="newsletter-sign-up-form" onSubmit={handleSubmit}>{t3}{t26}</form>;
    $[50] = handleSubmit;
    $[51] = t26;
    $[52] = t3;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  return t27;
}
function _temp3(previous_1) {
  return previous_1 - 1;
}
function _temp2(previous_0) {
  return previous_0 + 1;
}
function _temp(previous) {
  return previous + 1;
}